'use client'

import { clsx } from 'clsx'
import { type FC, useEffect, useId, useRef } from 'react'

import { IconClose } from '@redteclab/icons'

import { FormControl, FormLabel, Input } from 'base-ui'

import { ALGOLIA_QUERY_MAX_LENGTH } from '../../../../../algolia'
import { useHeaderSearchContext } from '../../headerSearchContext'
import { useHeaderSearchPlaceholderText } from '../../useHeaderSearchPlaceholderText'
import { handleKeyPressedOnSearchInput } from '../HeaderSearchActiveSearchBox/HeaderSearchAlgoliaContainer/HeaderSearchResultsContainer/HeaderSearchResults/handleKeyPressedOnSearchInput'

import { HeaderSearchMobileInputCloseSearchButton } from './HeaderSearchMobileInputCloseSearchButton'

interface HeaderSearchMobileInputProps {
  className: string
}

export const HeaderSearchMobileInput: FC<HeaderSearchMobileInputProps> = ({
  className,
}) => {
  const headerSearchContext = useHeaderSearchContext()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const { inputControls } = headerSearchContext
  const headerSearchPlaceholderText = useHeaderSearchPlaceholderText()
  const inputId = useId()
  useEffect(() => {
    /**
     * input when mobile search open
     */
    inputRef.current?.focus()
  }, [])

  return (
    <form
      className={clsx(
        'mb-5 flex rounded-full border border-dark-brand bg-light-primary-medium',
        className,
      )}
      onSubmit={(event): void => {
        /**
         * We do "submitting" via js.
         * Form is left as fallback, for cases when javascript will fail to hydrate for some reason,
         * but user still be able to land on search results page.
         */
        event.preventDefault()
        headerSearchContext.handleSubmitSearch()
      }}
    >
      <HeaderSearchMobileInputCloseSearchButton />
      <FormControl
        className="w-full"
        onChange={inputControls.handleInputChange}
        value={inputControls.searchInputValue}
      >
        <FormLabel htmlFor={inputId}>{headerSearchPlaceholderText}</FormLabel>
        <Input
          className="mr-6"
          data-qa-id="search-box-query"
          endAdornment={
            inputControls.searchInputValue === '' ? null : (
              <button
                onClick={() => {
                  inputControls.clearSearch()
                }}
                type="button"
              >
                <IconClose className="-mr-2 size-8 text-dark-brand" />
              </button>
            )
          }
          name="query"
          onKeyUp={(event): void => {
            handleKeyPressedOnSearchInput(event, headerSearchContext)
          }}
          ref={inputRef}
          slotProps={{
            input: {
              className: 'w-full',
              id: inputId,
              maxLength: ALGOLIA_QUERY_MAX_LENGTH,
            },
            root: {
              className: '!shadow-none border-none bg-transparent',
            },
          }}
          type="text"
        />
      </FormControl>
    </form>
  )
}
